/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

import React from 'react';
import { Modal } from 'react-bootstrap';
import { useTheme } from '../../theme/ThemeContext';

export default function ThemeSelectionModal({ open, onClose }) {
    const { theme: currentTheme, switchTheme, themesList } = useTheme();

    // Use current theme for modal styling to keep it consistent
    const modalStyle = {
        borderRadius: currentTheme.cardRadius,
        overflow: 'hidden',
        border: 'none',
        boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
    };

    const headerStyle = {
        background: currentTheme.primary,
        color: '#fff',
        borderBottom: 'none',
        padding: '16px 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
    };

    return (
        <Modal
            show={open}
            onHide={onClose}
            centered
            contentClassName="border-0 shadow-lg"
            style={{ zIndex: 1060 }} // High z-index as requested
        >
            <Modal.Header closeButton style={headerStyle} closeVariant="white">
                <Modal.Title style={{ fontSize: '1.1rem', fontWeight: 600 }}>Switch Theme</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ padding: '24px', background: '#f8f9fa' }}>
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))',
                    gap: 16
                }}>
                    {themesList.map((t) => {
                        const isActive = currentTheme.name === t.name;
                        return (
                            <div
                                key={t.name}
                                onClick={() => {
                                    switchTheme(t);
                                    // Optional: Close on select or let user see change?
                                    // "Once theme is selected... store... apply"
                                    // Usually users expect immediate feedback. 
                                    // Let's keep it open or close? 
                                    // "This selection dialog..."
                                    // I'll keep it open to let them browse, but functionality applies immediately.
                                }}
                                style={{
                                    cursor: 'pointer',
                                    borderRadius: currentTheme.cardRadius,
                                    border: isActive ? `2px solid ${currentTheme.primary}` : '1px solid #e0e0e0',
                                    background: '#fff',
                                    overflow: 'hidden',
                                    transition: 'transform 0.2s, box-shadow 0.2s',
                                    position: 'relative',
                                    transform: isActive ? 'scale(1.02)' : 'none',
                                    boxShadow: isActive ? `0 4px 12px ${currentTheme.primary}40` : '0 2px 4px rgba(0,0,0,0.05)'
                                }}
                                onMouseEnter={(e) => {
                                    if (!isActive) e.currentTarget.style.transform = 'translateY(-2px)';
                                }}
                                onMouseLeave={(e) => {
                                    if (!isActive) e.currentTarget.style.transform = 'none';
                                }}
                            >
                                {/* Theme Preview (swatch) */}
                                <div style={{
                                    height: 80,
                                    background: t.gradientBackground || t.primary,
                                    position: 'relative',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    {/* Sidebar accent representation */}
                                    <div style={{
                                        position: 'absolute',
                                        left: 0,
                                        top: 0,
                                        bottom: 0,
                                        width: 12,
                                        background: t.sidebar
                                    }} />

                                    {/* Secondary accent bubble */}
                                    <div style={{
                                        width: 24,
                                        height: 24,
                                        borderRadius: '50%',
                                        background: t.secondary,
                                        boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
                                    }} />

                                    {isActive && (
                                        <div style={{
                                            position: 'absolute',
                                            top: 6,
                                            right: 6,
                                            background: '#fff',
                                            borderRadius: '50%',
                                            width: 20,
                                            height: 20,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: t.primary
                                        }}>
                                            <i className="bi bi-check" style={{ fontSize: 16, fontWeight: 'bold' }}></i>
                                        </div>
                                    )}
                                </div>

                                {/* Theme Label */}
                                <div style={{
                                    padding: '10px 8px',
                                    textAlign: 'center',
                                    fontWeight: 600,
                                    fontSize: '0.85rem',
                                    color: isActive ? t.primary : '#555',
                                    textTransform: 'capitalize'
                                }}>
                                    {t.name}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </Modal.Body>
        </Modal>
    );
}
